/** @jsx jsx */
import {
  Button,
  Grid,
  Hr,
  Pagination,
  Spacer,
  TextGroupMedium,
  TextGroupSmall,
  TextToolTip,
} from "@cd2/components"
import { Fields } from "@cd2/forms"
import { P, TextBlock } from "@cd2/text"
import { doo, IComponentProp, IxRef, titleize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useImperativeHandle, useRef, useState } from "react"
import { icons } from "../../../constants/icons"
import { useConfirmation } from "../../../hooks/useConfirmation"
import { NetworkStatus, useFetch } from "../../../hooks/useFetchOld"
import { useFetchOne } from "../../../hooks/useFetchOneOld"
import { IRecord } from "../../../types/schema"
import { useGlobalState } from "../../GlobalStateProvider"
import { AddressCard } from "../../Misc/AddressCard"
import { IAddressFormProps } from "./AddressForm"
import { CustomerAddressFormModal } from "./CustomerAddressFormModal"
import { brandColors } from "../../EOSThemeProvider"
import { FloatingBox } from "../../Misc/FloatingBox"
import { useSettings } from "../../../utils/useSettings"

type IAddress = IRecord<
  "addresses",
  | "label"
  | "name"
  | "contact"
  | "line_1"
  | "line_2"
  | "line_3"
  | "line_4"
  | "city"
  | "county"
  | "country"
  | "postcode"
  | "email"
  | "phone"
  | "importer_id"
>
type ICustomerWithAddresses = IRecord<"customers"> & {
  addresses: IAddress[]
}

interface IProps {
  customerId: string
  selectedAddress: IAddress
  heading?: React.ReactNode
  body?: React.ReactNode
  deliveryService?: string
  xRef?: IxRef<{ refetch: () => void }>
  errors?: Record<string, string>

  onAddressSelect(address: Partial<IAddress>): void

  onAddressOverride(address: Partial<IAddress>): void

  onAfterMutateAddress?(): void

  onClearAddress?(): void
}

export function AddressesSectionAsForm({
  customerId,
  selectedAddress,
  onAddressSelect,
  onAddressOverride,
  onAfterMutateAddress,
  onClearAddress,
  heading = "Choose address",
  body = "Select an address from the customer addresses below. Alternatively, you can save a one-off address to not associate the address with this customer.",
  xRef,
  deliveryService,
  errors,
}: IProps) {
  const settings = useSettings()
  const skeem = window.skeem
  const { currentUser } = useGlobalState()

  const [cachedOneTimeAddress, setCachedOneTimeAddress] = useState<IAddress | null>()
  const [query, setQuery] = useState("")

  const [dataPage, setDataPage] = useState(1)

  const dataFetch = useFetchOne<ICustomerWithAddresses>(
    ({ customerId }) => ({
      customers: {
        attributes: [
          "name",
          {
            name: "addresses",
            attributes: [
              "label",
              "name",
              "contact",
              "line_1",
              "line_2",
              "line_3",
              "line_4",
              "city",
              "county",
              "country",
              "postcode",
              "email",
              "phone",
              "importer_id",
            ],
            filter: {
              and: [
                { matches: { showInCustomerAddressOptions: true } },
                { not: { matches: { line_1: "" } } },
              ],
            },
            sort: {
              by: "isDefault",
              direction: "DESC",
            },
          },
        ],
        filter: { matches: { id: customerId } },
      },
    }),
    {
      variables: { customerId },
    },
  )

  const addressBookFetch = useFetch<IAddress>(
    ({ query }) => ({
      addresses: {
        attributes: [
          "label",
          "name",
          "contact",
          "line_1",
          "line_2",
          "line_3",
          "line_4",
          "city",
          "county",
          "country",
          "postcode",
          "email",
          "phone",
          "importer_id",
        ],
        filter: {
          and: [
            { matches: { stored: true, branch: currentUser.branch.id } },
            { scope: { name: "search", params: { query } } },
            {
              or: [
                { not: { matches: { customer: customerId } } },
                { isNull: { attr: "customer" } },
              ],
            },
          ],
        },
        paginate: { page: 1, size: 6 },
      },
    }),
    {
      variables: { query },
    },
  )

  const fetchRef = useRef<() => void>(dataFetch.refetch)
  fetchRef.current = dataFetch.refetch
  useImperativeHandle(xRef, () => ({ refetch: () => fetchRef.current() }))

  const debounceRef = useRef<any>(null)

  useEffect(() => {
    if (dataFetch.networkStatus === NetworkStatus.loading) {
      return
    }

    setCachedOneTimeAddress(null)
    dataFetch.refetch({ customerId })
    setDataPage(1)
  }, [customerId])

  useEffect(() => {
    setDataPage(1)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      addressBookFetch.refetch({ query })
    }, 500)

    return () => clearTimeout(debounceRef.current)
  }, [query])

  function findMatchingAddress(addresses: IAddress[], address: IAddress): IAddress | undefined {
    return addresses.find(addressFromArray => isMatchingAddress(addressFromArray, address))
  }

  function isMatchingAddress(add1: IAddress, add2: IAddress): boolean {
    const keys: (keyof IAddress)[] = [
      "label",
      "name",
      "line_1",
      "line_2",
      "line_3",
      "line_4",
      "city",
      "county",
      "country",
      "postcode",
      "phone",
      "email",
      "contact",
    ]
    return keys.every(key => add1[key] === add2[key])
  }

  const isLoading = dataFetch.networkStatus === NetworkStatus.loading

  if (dataFetch.error) {
    throw dataFetch.error
  }

  const hasValidSelectedAddress =
    selectedAddress &&
    Object.entries(selectedAddress).filter(([key, value]) => key !== "id" && !!value).length > 0

  const data = dataFetch.data && {
    id: dataFetch.data.id,
    addresses: dataFetch.data.addresses.filter(address =>
      Object.entries(address).some(
        ([key, value]) =>
          !["id", "importer_id"].includes(key) &&
          value?.toLowerCase().includes(query.toLowerCase()),
      ),
    ),
  }

  const selectedAddressId =
    selectedAddress && data ? findMatchingAddress(data.addresses, selectedAddress)?.id : undefined

  function isOneTimeAddress(address: IAddress) {
    return data && !findMatchingAddress(data.addresses, address)
  }

  function handleStoredAddressClick(address: IAddress) {
    // if we currently have a selected address and it is not stored, then cache it
    if (hasValidSelectedAddress && isOneTimeAddress(selectedAddress)) {
      setCachedOneTimeAddress({ ...selectedAddress })
    }
    onAddressSelect({ ...address })
  }

  async function handleStoreOneTimeAddress() {
    await skeem.mutate({
      addresses: {
        create: {
          ...selectedAddress,
          id: undefined,
          showInCustomerAddressOptions: true,
          customer: { set: customerId },
          branch: { set: currentUser.branch.id },
        },
      },
    })
    dataFetch.refetch()
  }

  const [addressModalNode, getAddressFromModal] = useConfirmation<
    {
      heading: string
      address: Partial<IAddress>
      actions: IAddressFormProps<string>["actions"]
      suggestedEmails?: string[]
    },
    { data: Partial<IAddress>; action: string } | null
  >(({ onResolve, data }) => (
    <CustomerAddressFormModal
      customerId={customerId}
      onCancel={() => onResolve(null)}
      heading={data.heading}
      body="Enter the address details below"
      onSubmit={(data, action) => onResolve({ data, action })}
      initialData={{ ...data.address }}
      actions={data.actions || []}
      suggestedEmails={data.suggestedEmails}
      fieldMaxLength={doo(() => {
        if (!settings.useAddressLengthValidation) {
          return undefined
        }
        return deliveryService?.toLowerCase()?.includes("dpd")
          ? 35
          : deliveryService?.toLowerCase()?.includes("fedex")
          ? 35
          : undefined
      })}
      isOpen
    />
  ))

  async function handleEditAddress(address: IAddress) {
    const oneTimeAddress = isOneTimeAddress(address)
    const response = await getAddressFromModal({
      heading: "Edit address",
      address: address,
      actions: oneTimeAddress
        ? [{ children: "Update Address", name: "update_one_time" }]
        : [
            address.importer_id
              ? {
                  children: "Update stored address",
                  name: "update_stored",
                  disabled: true,
                  info: "This address is linked to one in Sage and cannot be edited.",
                }
              : { children: "Update stored address", name: "update_stored" },
            { children: "Use for this quote only", name: "one_time" },
          ],
    })
    if (response === null) {
      return
    }

    const { data, action } = response

    if (action === "update_one_time" || action === "one_time") {
      setCachedOneTimeAddress(null)
      onAddressSelect({ ...data, importer_id: undefined })
    } else if (action === "update_stored") {
      await skeem.mutate({ addresses: { update: { id: address.id, ...data } } })

      dataFetch.refetch()

      onAfterMutateAddress?.()
      onAddressSelect({ ...data })
    } else {
      throw new Error(`Unknown action when updating address: "${action}"`)
    }
  }

  async function handleDeleteAddress(id: string) {
    await skeem.mutate({ addresses: { destroy: id } })
    dataFetch.refetch()
    onAfterMutateAddress?.()
  }

  async function handleCreateAddress() {
    const contacts = await skeem.fetch2<IRecord<"contacts", "email">[]>({
      model: {
        name: "contacts",
        attributes: ["email"],
        filter: { matches: { customer: customerId } },
      },
    })
    const response = await getAddressFromModal({
      heading: "Create new address",
      suggestedEmails: contacts.map(c => c.email),
      address: {},
      actions: [
        { children: "Use for this quote only", name: "one_time" },
        { children: "Store permanently", name: "store_new" },
      ],
    })
    if (response === null) {
      return
    }

    const { data, action } = response

    if (action === "one_time") {
      onAddressOverride(data)
      setCachedOneTimeAddress(null)
    } else if (action === "store_new") {
      await skeem.mutate({
        customers: {
          update: {
            id: customerId,
            addresses: {
              create: {
                ...data,
                showInCustomerAddressOptions: true,
                branch: { set: currentUser.branch.id },
              },
            },
          },
        },
      })
      dataFetch.refetch()
      onAfterMutateAddress?.()
      onAddressOverride(data)
    } else {
      throw new Error(`Unknown action when creating address: "${action}"`)
    }
  }

  function handleToggleOffAddress() {
    onClearAddress?.()
  }

  return (
    <FloatingBox>
      {addressModalNode}
      <Grid columns={["auto", "max-content"]} gap={16}>
        <div>
          <div css={css("max-width: 500px")}>
            <TextGroupMedium heading={heading} body={body} />
            <Spacer size={24} />
            <Fields.Search value={query} onChange={setQuery} />
          </div>
        </div>
        <div>
          <Button
            rightIcon={icons.addresses}
            onClick={() => handleCreateAddress()}
            buttonColor={brandColors.lightNight}
          >
            Add new address
          </Button>
        </div>
      </Grid>
      <Spacer size={24} />
      {errors && typeof errors == "object" && Object.keys(errors).length > 0 && (
        <div>
          {Object.keys(errors).map(key => (
            <P key={key} size="small" color="red">
              {titleize(key)} {errors[key]}
            </P>
          ))}
          <Spacer />
        </div>
      )}

      {!hasValidSelectedAddress && !data?.addresses?.length && (
        <i
          css={css`
            font-size: 12px;
          `}
        >
          {query ? (
            <div>
              Customer has no previously saved addresses matching <b>{query}</b>
            </div>
          ) : (
            <div>Customer has no previously saved addresses</div>
          )}
        </i>
      )}
      {!hasValidSelectedAddress && (
        <React.Fragment>
          <i
            css={css`
              font-size: 12px;
            `}
          >
            No address selected
          </i>
          <Spacer />
        </React.Fragment>
      )}
      <Grid columns={[{ fr: 1 }, { fr: 1 }, { fr: 1 }]} gap={16}>
        {cachedOneTimeAddress && !isMatchingAddress(cachedOneTimeAddress, selectedAddress) && (
          <OneTimeAddressCard
            address={cachedOneTimeAddress}
            onClick={() => handleStoredAddressClick(cachedOneTimeAddress)}
            heading="Non-stored addresses"
            body={
              <TextBlock color="primary">
                <P>
                  This address appears only on this form. To have this address available everywhere,
                  select store address from the menu.
                </P>
                <P>As this address is not selected, it will be removed on save</P>
              </TextBlock>
            }
          />
        )}
        {hasValidSelectedAddress && isOneTimeAddress(selectedAddress) && (
          <OneTimeAddressCard
            address={{ ...selectedAddress }}
            onEditClick={() => handleEditAddress({ ...selectedAddress })}
            menuItems={[{ children: "Store address", onClick: handleStoreOneTimeAddress }]}
            heading="Non-stored addresses"
            body="This address appears only on this form. To have this address available everywhere, select store address from the menu."
            isSelected
          />
        )}
        {isLoading
          ? Array(4)
              .fill(null)
              .map((_, i) => <AddressCard key={i} address={{ line_1: "" }} isLoading />)
          : data.addresses.slice((dataPage - 1) * 6, 6 * dataPage).map(address => (
              <AddressCard
                key={address.id}
                address={address}
                onEditClick={
                  selectedAddressId === address.id ? () => handleEditAddress(address) : undefined
                }
                onRemoveClick={
                  selectedAddressId === address.id ? handleToggleOffAddress : undefined
                }
                isSelected={selectedAddressId === address.id}
                onClick={
                  selectedAddressId === address.id
                    ? handleToggleOffAddress
                    : () => handleStoredAddressClick(address)
                }
                menuItems={[
                  !address.importer_id &&
                    selectedAddressId !== address.id && {
                      children: "Delete",
                      onClick: () => handleDeleteAddress(address.id),
                    },
                ].filter(Boolean)}
              />
            ))}
      </Grid>
      <Spacer />
      {(data?.addresses || []).length > 6 && (
        <Pagination
          currentPage={dataPage}
          maxPages={Math.ceil((data?.addresses || []).length / 6)}
          gotoPage={page => setDataPage(page)}
        />
      )}
      {query && (
        <React.Fragment>
          <Spacer size={16} />
          <Hr />
          <Spacer size={16} />
          <TextGroupSmall
            heading={"Address Book"}
            body="Addresses that match your search that don't appear in this customer's addresses"
          />
          <Spacer />
          <Grid columns={[{ fr: 1 }, { fr: 1 }, { fr: 1 }]} gap={16}>
            {addressBookFetch.networkStatus === NetworkStatus.loading
              ? Array(4)
                  .fill(null)
                  .map((_, i) => (
                    <AddressCard
                      key={i}
                      address={{ line_1: "" }}
                      onEditClick={() => {}}
                      isLoading
                    />
                  ))
              : (addressBookFetch.data ?? []).map(address => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    onClick={() => {
                      setCachedOneTimeAddress(null)
                      onAddressSelect({ ...address })
                    }}
                  />
                ))}
          </Grid>
          {addressBookFetch.networkStatus !== NetworkStatus.loading &&
            (addressBookFetch.data ?? []).length === 0 && (
              <i
                css={css`
                  font-size: 12px;
                `}
              >
                No results for <b>{(addressBookFetch.variables as any).query}</b> in address book.
              </i>
            )}
        </React.Fragment>
      )}
    </FloatingBox>
  )
}

// When Editing address have option to save to customer / save as new address
// Duplicate - save as one time doesnt work

function OneTimeAddressCard({
  address,
  onClick,
  menuItems,
  heading,
  body,
  isSelected,
  onEditClick,
}: {
  address: Partial<IAddress>
  onClick?(): void
  onEditClick?(): void
  menuItems?: IComponentProp<typeof AddressCard, "menuItems">
  heading: IComponentProp<typeof TextGroupMedium, "heading">
  body: IComponentProp<typeof TextGroupMedium, "body">
  isSelected?: boolean
}) {
  return (
    <div
      css={css`
        background-color: #e5e5e5;
        padding: 8px;
        border-radius: 8px;
      `}
    >
      <AddressCard
        address={address}
        onClick={onClick}
        onEditClick={onEditClick}
        menuItems={menuItems}
        highlightColor="#444"
        isSelected={isSelected}
      />
      <Spacer />
      <div
        css={css`
          font-size: 12px;
          text-align: right;
        `}
      >
        <TextToolTip
          hoverContent={() => (
            <div
              css={css`
                width: 200px;
              `}
            >
              <TextGroupSmall heading={heading} body={body} />
            </div>
          )}
        >
          Non-stored addresses
        </TextToolTip>
      </div>
    </div>
  )
}
